/** Backup screen: export all sets as a JSON file and import one back. */
import { el, page } from '../dom';
import { navigate } from '../router';
import { getSets } from '../../core/storage';
import { downloadExport, importSets, parseExportFile, type ImportStrategy } from '../../core/exchange';

export function transferScreen(): HTMLElement {
  const sets = getSets();
  const status = el('p', { class: 'hint' });

  let strategy: ImportStrategy = 'merge';

  const fileInput = el('input', {
    class: 'input',
    type: 'file',
    accept: 'application/json,.json',
    id: 'import-file',
  }) as HTMLInputElement;

  fileInput.addEventListener('change', async () => {
    const file = fileInput.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const incoming = parseExportFile(text);
      const summary = importSets(incoming, strategy);

      status.className = 'hint hint-ok';
      status.textContent =
        `${summary.setsAdded} Set(s) neu, ${summary.setsMerged} zusammengeführt, ` +
        `${summary.cardsAdded} Wörter neu, ${summary.cardsUpdated} aktualisiert.`;

      setTimeout(() => navigate('/sets'), 1200);
    } catch (error) {
      status.className = 'hint hint-error';
      status.textContent =
        error instanceof Error ? error.message : 'Die Datei konnte nicht gelesen werden.';
    } finally {
      fileInput.value = '';
    }
  });

  const strategyChips = (
    [
      { value: 'merge' as const, label: 'Zusammenführen', hint: 'Fortschritt behalten' },
      { value: 'replace' as const, label: 'Ersetzen', hint: 'Set überschreiben' },
    ]
  ).map((option) =>
    el(
      'button',
      {
        class: `chip${option.value === strategy ? ' chip-active' : ''}`,
        type: 'button',
        'aria-pressed': option.value === strategy,
        onclick: (event: Event) => {
          const target = event.currentTarget as HTMLElement;
          target.parentElement?.querySelectorAll('.chip').forEach((chip) => {
            chip.classList.remove('chip-active');
            chip.setAttribute('aria-pressed', 'false');
          });
          target.classList.add('chip-active');
          target.setAttribute('aria-pressed', 'true');
          strategy = option.value;
        },
      },
      [
        el('span', { class: 'chip-label', text: option.label }),
        el('span', { class: 'chip-hint', text: option.hint }),
      ],
    ),
  );

  return page({
    title: 'Sichern & Laden',
    back: '/sets',
    children: [
      el('div', { class: 'card' }, [
        el('h2', { class: 'section-title', text: 'Sichern' }),
        el('p', {
          class: 'hint',
          text: 'Lädt alle Sets samt Fortschritt als JSON-Datei herunter.',
        }),
        el('button', {
          class: 'btn btn-primary btn-big',
          type: 'button',
          text: `Alle Sets exportieren (${sets.length})`,
          disabled: sets.length === 0,
          onclick: () => downloadExport(getSets()),
        }),
      ]),
      el('div', { class: 'card' }, [
        el('h2', { class: 'section-title', text: 'Laden' }),
        el('p', { class: 'hint', text: 'Wähle eine zuvor exportierte Voci-Datei aus.' }),
        el('div', { class: 'chips' }, strategyChips),
        el('label', { class: 'label', for: 'import-file', text: 'Datei auswählen' }),
        fileInput,
        status,
      ]),
    ],
  });
}
